import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2, MessageSquare, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearnerSurvey() {
  const { surveyId } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const trackId = searchParams.get("track_id");
  const cohortId = searchParams.get("cohort_id");

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/learner/surveys/${surveyId}`, { withCredentials: true });
      setSurvey(data);
      if (data.already_submitted && !data.allow_resubmit) {
        setCompleted(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Survey not available");
      nav(-1);
    } finally {
      setLoading(false);
    }
  }, [surveyId, nav]);

  useEffect(() => { load(); }, [load]);

  const setMCResponse = (itemId, optionId) => {
    setResponses((prev) => ({ ...prev, [itemId]: { selected_option_ids: [optionId] } }));
  };

  const toggleSATAOption = (itemId, optionId) => {
    setResponses((prev) => {
      const cur = prev[itemId]?.selected_option_ids || [];
      const next = cur.includes(optionId) ? cur.filter((x) => x !== optionId) : [...cur, optionId];
      return { ...prev, [itemId]: { selected_option_ids: next } };
    });
  };

  const setLikertValue = (itemId, value) => {
    setResponses((prev) => ({ ...prev, [itemId]: { likert_value: value } }));
  };

  const isItemAnswered = (item) => {
    const r = responses[item.id];
    if (!r) return false;
    if (item.item_type === "likert") return r.likert_value != null;
    return (r.selected_option_ids || []).length > 0;
  };

  const answeredCount = (survey?.items || []).filter(isItemAnswered).length;
  const totalItems = survey?.items?.length || 0;

  const handleSubmit = async () => {
    if (!survey) return;
    const allItems = survey.items || [];
    const unansweredCount = allItems.filter((it) => !isItemAnswered(it)).length;
    if (unansweredCount > 0) {
      toast.error(`All ${allItems.length} questions are required — ${unansweredCount} unanswered.`);
      return;
    }
    const payload = {
      track_id: trackId || null,
      cohort_id: cohortId || null,
      responses: allItems.map((it) => ({
        item_id: it.id,
        ...(responses[it.id] || {}),
      })),
    };
    setSubmitting(true);
    try {
      await axios.post(`${API}/learner/surveys/${surveyId}/submit`, payload, { withCredentials: true });
      setCompleted(true);
      toast.success("Thank you — your feedback has been recorded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
    );
  }

  if (completed) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 py-8" data-testid="survey-complete">
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-indigo-50 mx-auto flex items-center justify-center">
            <CheckCircle2 className="h-7 w-7" style={{ color: "var(--theme-primary, var(--primary-color, #6366F1))" }} />
          </div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Thank you</h1>
          <p className="text-sm font-body text-gray-500 max-w-md mx-auto">
            Your feedback has been recorded. You can proceed with the rest of your learning experience.
          </p>
          <Button
            onClick={() => nav(trackId ? `/portal/tracks/${trackId}` : "/portal/dashboard")}
            className="mt-4 text-xs font-body gap-1.5"
            data-testid="survey-complete-continue"
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  if (!survey) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="learner-survey">
      <button
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1.5 text-xs font-body text-gray-500 hover:text-gray-700 hover:underline"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 space-y-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" style={{ color: "var(--theme-primary, var(--primary-color, #6366F1))" }} />
          <span className="text-[11px] font-body uppercase tracking-wider text-gray-500">Survey</span>
          {survey.anonymous && (
            <span className="text-[10px] font-body px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
              Anonymous
            </span>
          )}
        </div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">{survey.title}</h1>
        {survey.description && (
          <p className="text-sm font-body text-gray-500">{survey.description}</p>
        )}
        <p className="text-xs font-body text-gray-400 pt-2">
          All questions are required — please answer each one to submit.
        </p>
      </div>

      <div className="space-y-4">
        {(survey.items || []).map((item, idx) => (
          <div
            key={item.id}
            className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 space-y-4"
            data-testid={`survey-item-${item.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                <span className="text-xs font-body font-bold text-indigo-600">{idx + 1}</span>
              </div>
              <div className="flex-1 pt-0.5">
                <p className="text-base font-body font-semibold text-gray-900">{item.title}</p>
                {item.stem && (
                  <div
                    className="text-sm font-body text-gray-600 mt-1 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.stem) }}
                  />
                )}
              </div>
            </div>

            {/* MC */}
            {item.item_type === "mc" && (
              <div className="space-y-2">
                {(item.answer_options || []).map((opt) => {
                  const selected = (responses[item.id]?.selected_option_ids || [])[0] === opt.option_id;
                  return (
                    <button
                      key={opt.option_id}
                      onClick={() => setMCResponse(item.id, opt.option_id)}
                      className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-body transition-all ${
                        selected
                          ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                          : "border-gray-200 hover:border-gray-300 text-gray-700"
                      }`}
                      data-testid={`survey-mc-opt-${item.id}-${opt.option_id}`}
                    >
                      {opt.text}
                    </button>
                  );
                })}
              </div>
            )}

            {/* SATA */}
            {item.item_type === "sata" && (
              <div className="space-y-2">
                {(item.answer_options || []).map((opt) => {
                  const selected = (responses[item.id]?.selected_option_ids || []).includes(opt.option_id);
                  return (
                    <label
                      key={opt.option_id}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-body cursor-pointer transition-all ${
                        selected
                          ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                          : "border-gray-200 hover:border-gray-300 text-gray-700"
                      }`}
                      data-testid={`survey-sata-opt-${item.id}-${opt.option_id}`}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleSATAOption(item.id, opt.option_id)}
                      />
                      <span>{opt.text}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Likert 1–5 */}
            {item.item_type === "likert" && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between text-[10px] font-body uppercase tracking-wider text-gray-400">
                  <span>Strongly disagree</span>
                  <span>Strongly agree</span>
                </div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((v) => {
                    const selected = responses[item.id]?.likert_value === v;
                    return (
                      <button
                        key={v}
                        onClick={() => setLikertValue(item.id, v)}
                        className={`flex-1 h-11 rounded-xl border text-sm font-body font-medium transition-all ${
                          selected
                            ? "border-indigo-500 bg-indigo-500 text-white"
                            : "border-gray-200 hover:border-gray-300 text-gray-600"
                        }`}
                        data-testid={`survey-likert-${item.id}-${v}`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between shadow-sm">
        <p className="text-xs font-body text-gray-500" data-testid="survey-progress">
          {answeredCount} of {totalItems} answered
          {answeredCount < totalItems && <span className="ml-1 text-amber-600">· {totalItems - answeredCount} remaining</span>}
        </p>
        <Button
          onClick={handleSubmit}
          disabled={submitting || answeredCount < totalItems || totalItems === 0}
          className="text-xs font-body gap-1.5"
          data-testid="survey-submit-btn"
        >
          {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
          <Sparkles className="h-3 w-3" />
          Submit Feedback
        </Button>
      </div>
    </div>
  );
}
